import { useState } from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Globe, HelpCircle, FileText, Upload, Plus, Trash2, Loader2 } from "lucide-react";
import { useKnowledgeBases, useKnowledgeItems, useCreateKnowledgeBase, useCreateKnowledgeItem, useDeleteKnowledgeItem, useDeleteKnowledgeBase } from "@/hooks/useAITraining";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";

export default function KnowledgeBasePanel() {
  const [selectedKbId, setSelectedKbId] = useState<string | null>(null);
  const [modalType, setModalType] = useState<'url' | 'faq' | 'text' | 'file' | null>(null);
  const [createKbModal, setCreateKbModal] = useState(false);

  const { data: knowledgeBases, isLoading: loadingKbs } = useKnowledgeBases();
  const { data: knowledgeItems, isLoading: loadingItems } = useKnowledgeItems(selectedKbId);
  const createKb = useCreateKnowledgeBase();
  const createItem = useCreateKnowledgeItem();
  const deleteItem = useDeleteKnowledgeItem();
  const deleteKb = useDeleteKnowledgeBase();

  // Form states
  const [kbName, setKbName] = useState("");
  const [kbType, setKbType] = useState<string>("web_crawler");
  const [url, setUrl] = useState("");
  const [question, setQuestion] = useState("");
  const [answer, setAnswer] = useState("");
  const [textContent, setTextContent] = useState("");
  const [textTitle, setTextTitle] = useState("");
  const [crawling, setCrawling] = useState(false);

  const handleCreateKb = async () => {
    if (!kbName.trim()) {
      toast.error("Please enter a name");
      return;
    }
    await createKb.mutateAsync({ name: kbName, type: kbType });
    setCreateKbModal(false);
    setKbName("");
    setKbType("web_crawler");
  };

  const handleAddUrl = async () => {
    if (!url.trim() || !selectedKbId) return;
    
    setCrawling(true);
    try {
      // Call crawl-url edge function
      const { data, error } = await supabase.functions.invoke('crawl-url', {
        body: { url }
      });

      if (error) throw error;

      await createItem.mutateAsync({
        knowledge_base_id: selectedKbId,
        type: 'url',
        source_url: url,
        content: data.content,
        metadata: { title: data.title, crawled_at: new Date().toISOString() }
      });

      setModalType(null);
      setUrl("");
    } catch (error: any) {
      toast.error(`Failed to crawl URL: ${error.message}`);
    } finally {
      setCrawling(false);
    }
  };

  const handleAddFaq = async () => {
    if (!question.trim() || !answer.trim() || !selectedKbId) return;

    await createItem.mutateAsync({
      knowledge_base_id: selectedKbId,
      type: 'faq',
      question,
      answer,
      content: `Q: ${question}\nA: ${answer}`
    });

    setModalType(null);
    setQuestion("");
    setAnswer("");
  };

  const handleAddText = async () => {
    if (!textContent.trim() || !selectedKbId) return;

    await createItem.mutateAsync({
      knowledge_base_id: selectedKbId,
      type: 'text',
      content: textContent,
      metadata: { title: textTitle || 'Untitled' }
    });

    setModalType(null);
    setTextContent("");
    setTextTitle("");
  };

  const getKbIcon = (type: string) => {
    switch (type) {
      case 'web_crawler': return <Globe className="h-8 w-8" />;
      case 'faq': return <HelpCircle className="h-8 w-8" />;
      case 'rich_text': return <FileText className="h-8 w-8" />;
      case 'file_upload': return <Upload className="h-8 w-8" />;
      default: return <FileText className="h-8 w-8" />;
    }
  };

  const getItemCount = (kb: any) => {
    return kb.ai_knowledge_items?.[0]?.count || 0;
  };

  if (loadingKbs) {
    return <div className="flex items-center justify-center p-8"><Loader2 className="h-6 w-6 animate-spin" /></div>;
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Select value={selectedKbId || ""} onValueChange={setSelectedKbId}>
            <SelectTrigger className="w-[300px]">
              <SelectValue placeholder="Select Knowledge Base" />
            </SelectTrigger>
            <SelectContent>
              {knowledgeBases?.map((kb) => (
                <SelectItem key={kb.id} value={kb.id}>{kb.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Button onClick={() => setCreateKbModal(true)} size="sm">
            <Plus className="h-4 w-4 mr-2" />
            Create New
          </Button>
          {selectedKbId && (
            <Button 
              onClick={() => {
                if (confirm("Delete this knowledge base and all its items?")) {
                  deleteKb.mutate(selectedKbId);
                  setSelectedKbId(null);
                }
              }} 
              variant="ghost" 
              size="sm"
            >
              <Trash2 className="h-4 w-4" />
            </Button>
          )}
        </div>
      </div>

      {!selectedKbId ? (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {knowledgeBases?.map((kb) => (
            <Card key={kb.id} className="p-6 hover:border-primary cursor-pointer transition-colors" onClick={() => setSelectedKbId(kb.id)}>
              <div className="flex flex-col items-center text-center gap-3">
                {getKbIcon(kb.type)}
                <div>
                  <p className="font-semibold">{kb.name}</p>
                  <p className="text-sm text-muted-foreground">Items: {getItemCount(kb)}</p>
                </div>
              </div>
            </Card>
          ))}
        </div>
      ) : (
        <div className="space-y-4">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <Card className="p-6 hover:border-primary cursor-pointer transition-colors" onClick={() => setModalType('url')}>
              <div className="flex flex-col items-center text-center gap-3">
                <Globe className="h-8 w-8" />
                <p className="font-semibold">Add URL</p>
              </div>
            </Card>
            <Card className="p-6 hover:border-primary cursor-pointer transition-colors" onClick={() => setModalType('faq')}>
              <div className="flex flex-col items-center text-center gap-3">
                <HelpCircle className="h-8 w-8" />
                <p className="font-semibold">Add FAQ</p>
              </div>
            </Card>
            <Card className="p-6 hover:border-primary cursor-pointer transition-colors" onClick={() => setModalType('text')}>
              <div className="flex flex-col items-center text-center gap-3">
                <FileText className="h-8 w-8" />
                <p className="font-semibold">Add Text</p>
              </div>
            </Card>
          </div>

          {loadingItems ? (
            <div className="flex items-center justify-center p-8"><Loader2 className="h-6 w-6 animate-spin" /></div>
          ) : (
            <div className="space-y-2">
              {knowledgeItems?.map((item) => (
                <Card key={item.id} className="p-4 flex items-start justify-between">
                  <div className="flex-1">
                    {item.type === 'url' && (
                      <div>
                        <p className="font-medium">{(item.metadata as any)?.title || 'Untitled'}</p>
                        <p className="text-sm text-muted-foreground">{item.source_url}</p>
                      </div>
                    )}
                    {item.type === 'faq' && (
                      <div>
                        <p className="font-medium">{item.question}</p>
                        <p className="text-sm text-muted-foreground">{item.answer?.substring(0, 100)}...</p>
                      </div>
                    )}
                    {item.type === 'text' && (
                      <div>
                        <p className="font-medium">{(item.metadata as any)?.title || 'Rich Text'}</p>
                        <p className="text-sm text-muted-foreground">{item.content?.substring(0, 100)}...</p>
                      </div>
                    )}
                  </div>
                  <Button 
                    onClick={() => {
                      if (confirm("Delete this item?")) {
                        deleteItem.mutate(item.id);
                      }
                    }} 
                    variant="ghost" 
                    size="sm"
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </Card>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Create KB Modal */}
      <Dialog open={createKbModal} onOpenChange={setCreateKbModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Create Knowledge Base</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <label className="text-sm font-medium">Name</label>
              <Input value={kbName} onChange={(e) => setKbName(e.target.value)} placeholder="Product Knowledge" />
            </div>
            <div>
              <label className="text-sm font-medium">Type</label>
              <Select value={kbType} onValueChange={setKbType}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="web_crawler">Web Crawler</SelectItem>
                  <SelectItem value="faq">FAQ</SelectItem>
                  <SelectItem value="rich_text">Rich Text</SelectItem>
                  <SelectItem value="file_upload">File Upload</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button onClick={handleCreateKb}>Create</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* URL Modal */}
      <Dialog open={modalType === 'url'} onOpenChange={() => setModalType(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add URL to Crawl</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <label className="text-sm font-medium">URL</label>
              <Input value={url} onChange={(e) => setUrl(e.target.value)} placeholder="https://example.com" />
            </div>
          </div>
          <DialogFooter>
            <Button onClick={handleAddUrl} disabled={crawling}>
              {crawling ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
              {crawling ? 'Crawling...' : 'Add URL'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* FAQ Modal */}
      <Dialog open={modalType === 'faq'} onOpenChange={() => setModalType(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add FAQ</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <label className="text-sm font-medium">Question</label>
              <Input value={question} onChange={(e) => setQuestion(e.target.value)} placeholder="What is..." />
            </div>
            <div>
              <label className="text-sm font-medium">Answer</label>
              <Textarea value={answer} onChange={(e) => setAnswer(e.target.value)} placeholder="The answer is..." rows={4} />
            </div>
          </div>
          <DialogFooter>
            <Button onClick={handleAddFaq}>Add FAQ</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Text Modal */}
      <Dialog open={modalType === 'text'} onOpenChange={() => setModalType(null)}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Add Rich Text Content</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <label className="text-sm font-medium">Title (optional)</label>
              <Input value={textTitle} onChange={(e) => setTextTitle(e.target.value)} placeholder="Company Guidelines" />
            </div>
            <div>
              <label className="text-sm font-medium">Content</label>
              <Textarea value={textContent} onChange={(e) => setTextContent(e.target.value)} placeholder="Paste content here..." rows={10} />
            </div>
          </div>
          <DialogFooter>
            <Button onClick={handleAddText}>Add Content</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
